/**
 * Every number shown to the user goes through here: narrow no-break space as the
 * thousands separator, comma as the decimal mark.
 */

const NBSP = ' '; // narrow no-break space, the French thousands separator
/** U+2212, the typographic minus. A hyphen next to a digit reads as a dash. */
const MINUS = '−';

function frenchGroups(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

export function formatInt(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value < 0 ? MINUS : '';
  return sign + frenchGroups(String(Math.abs(Math.round(value))));
}

export function formatDecimal(value: number, digits = 2): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value < 0 ? MINUS : '';
  const fixed = Math.abs(value).toFixed(digits);
  const [int = '0', frac] = fixed.split('.');
  const body = frenchGroups(int);
  return sign + (frac !== undefined && digits > 0 ? `${body},${frac}` : body);
}

export function formatEuros(value: number, digits = 2): string {
  return `${formatDecimal(value, digits)}${NBSP}€`;
}

/** Large money amounts, abbreviated the way a jackpot is written. */
export function formatJackpot(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—';
  const euros = cents / 100;
  if (euros >= 1_000_000) return `${formatDecimal(euros / 1_000_000, 0)}${NBSP}M€`;
  if (euros >= 1000) return `${formatDecimal(euros / 1000, 0)}${NBSP}k€`;
  return formatEuros(euros, 0);
}

export function formatPercent(fraction: number, digits = 1): string {
  if (!Number.isFinite(fraction)) return '—';
  return `${formatDecimal(fraction * 100, digits)}${NBSP}%`;
}

/** Signed percentage, for a change relative to a baseline. */
export function formatSignedPercent(fraction: number, digits = 1): string {
  if (!Number.isFinite(fraction)) return '—';
  const sign = fraction >= 0 ? '+' : '';
  return `${sign}${formatDecimal(fraction * 100, digits)}${NBSP}%`;
}

/** "1 sur 19 068 840" */
export function formatOdds(oneIn: number): string {
  return `1${NBSP}sur${NBSP}${formatInt(oneIn)}`;
}

const LONG_DATE = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Paris',
});

const SHORT_DATE = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/Paris',
});

const MEDIUM_DATE = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Europe/Paris',
});

function isoToDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  // Midday UTC keeps the calendar day stable in any European timezone.
  return new Date(Date.UTC(y, m - 1, d, 12));
}

export function formatDateLong(iso: string): string {
  return LONG_DATE.format(isoToDate(iso));
}

export function formatDateShort(iso: string): string {
  return SHORT_DATE.format(isoToDate(iso));
}

/**
 * "3 sept. 2025" — carries the year, unlike formatDateShort. Used wherever two
 * dates are shown as a range: a twelve-month window runs from September to
 * September, and without the year "du 3 sept. au 2 sept." reads as inverted.
 */
export function formatDateMedium(iso: string): string {
  return MEDIUM_DATE.format(isoToDate(iso));
}

/** "2 j 04:12" — a countdown that stays readable at any distance. */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return 'en cours';
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  if (days > 0) return `${days}${NBSP}j ${pad(hours)}:${pad(minutes)}`;
  const seconds = Math.floor((ms % 60_000) / 1000);
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/** "il y a 3 jours" / "aujourd'hui" */
export function relativeDays(iso: string, now: Date = new Date()): string {
  const then = isoToDate(iso).getTime();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12);
  const days = Math.round((today - then) / 86_400_000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  return `il y a ${formatInt(days)} jours`;
}

export function formatBall(n: number): string {
  return String(n).padStart(2, '0');
}

/** A grid as shareable plain text. */
export function gridToText(
  main: readonly number[],
  bonus: readonly number[],
  bonusLabel: string,
): string {
  return `${main.map(formatBall).join(' - ')}   ${bonusLabel} : ${bonus.join(' - ')}`;
}
